import { forEach as lodashForEach } from "lodash";
import type { Moment } from "moment-timezone";
import { duration, utc } from "moment-timezone";

import { HeartbeatStatus } from "./HeartbeatStatus";
import type { IEquipmentMonitoring } from "./IEquipmentMonitoring";
import type { IHasMonitoring } from "./IHasMonitoring";
import type { ISiteMonitoring } from "./ISiteMonitoring";
import type { ISiteMonitoringSlim } from "./ISiteMonitoringSlim";
import { PeriodicImageUploadStatus } from "./PeriodicImageUploadStatus";

export class MonitoringFunctions {
    // Copied from Site.cs, which is the master
    public static readonly heartBeatWarningTimeoutMinutes = duration(15, "minutes");

    public static readonly heartBeatErrorTimeoutMinutes = duration(1, "hours");

    public static readonly imageWarningTimeoutMinutes = duration(1, "hours");

    public static readonly imageErrorTimeoutMinutes = duration(2, "hours");

    public static readonly eventWarningTimeoutMinutes = duration(1, "days");

    public static readonly eventErrorTimeoutMinutes = duration(1, "days");

    public static enhanceSiteMonitoring<T extends ISiteMonitoringSlim>(site: T): T {
        MonitoringFunctions.enhanceHeartbeatStatus(site, site.dtOldestHeartbeat);
        MonitoringFunctions.enhanceImageUploadStatus(site, site.dtOldestImage);
        MonitoringFunctions.enhanceMonitoringIcon(site);

        const siteMonitoring = site as any as ISiteMonitoring;
        if (siteMonitoring.siteEquipment !== undefined) {
            lodashForEach(siteMonitoring.siteEquipment, (se) => MonitoringFunctions.enhanceEquipment(se.equipment));
        }
        return site;
    }

    public static enhanceEquipment(equipment: IEquipmentMonitoring) {
        if (equipment) {
            MonitoringFunctions.enhanceHeartbeatStatus(equipment, equipment.dtLastHeartbeat);
            // Actually resides on SiteEquipment currently
            MonitoringFunctions.enhanceImageUploadStatus(equipment, equipment.dtLastImageUpdate);
            MonitoringFunctions.enhanceMonitoringIcon(equipment);
        }
    }

    public static enhanceMonitoringIcon<T extends IHasMonitoring>(site: T): T {
        const heartbeatSeverity = site.heartbeatStatus as number;
        const imageUploadSeverity = site.imageUploadStatus as number;
        const multiErrorIcon = "exclamation-circle";

        if (heartbeatSeverity === imageUploadSeverity) {
            if (site.heartbeatStatus === HeartbeatStatus.Normal || site.heartbeatStatus === HeartbeatStatus.Unknown) {
                site.monitoringIcon = site.heartbeatIcon;
            } else {
                site.monitoringIcon = multiErrorIcon;
            }
            site.monitoringIconClass = site.heartbeatIconClass;
        } else if (heartbeatSeverity > imageUploadSeverity) {
            if (
                site.imageUploadStatus === PeriodicImageUploadStatus.Normal ||
                site.imageUploadStatus === PeriodicImageUploadStatus.Unknown
            ) {
                site.monitoringIcon = site.heartbeatIcon;
            } else {
                site.monitoringIcon = multiErrorIcon;
            }
            site.monitoringIconClass = site.heartbeatIconClass;
        } else if (heartbeatSeverity < imageUploadSeverity) {
            if (site.heartbeatStatus === HeartbeatStatus.Normal || site.heartbeatStatus === HeartbeatStatus.Unknown) {
                site.monitoringIcon = site.imageUploadIcon;
            } else {
                site.monitoringIcon = multiErrorIcon;
            }
            site.monitoringIconClass = site.imageUploadIcon;
        } else {
            // eslint-disable-next-line
            console.error(
                "Logic clause missing in MonitoringFunctions! HeartbeatStatus / ImageStatus are ",
                site.heartbeatStatus,
                site.imageUploadStatus,
            );
        }
        return site;
    }

    public static enhanceHeartbeatStatus<T extends IHasMonitoring>(site: T, dtHeartbeat?: Moment): T {
        site.heartbeatStatus = MonitoringFunctions.calculateHeartbeatStatus(dtHeartbeat);
        site.heartbeatStatusName = HeartbeatStatus[site.heartbeatStatus];

        switch (site.heartbeatStatus) {
            case HeartbeatStatus.Normal:
                site.heartbeatIcon = "check";
                site.heartbeatIconClass = "normal";
                break;
            case HeartbeatStatus.Warning:
                site.heartbeatIcon = "clock";
                site.heartbeatIconClass = "warning";
                break;
            case HeartbeatStatus.Error:
                site.heartbeatIcon = "clock";
                site.heartbeatIconClass = "error";
                break;
            default:
                site.heartbeatIcon = "question-circle";
                site.heartbeatIconClass = "unknown";
                break;
        }
        return site;
    }

    public static enhanceImageUploadStatus<T extends IHasMonitoring>(site: T, dtImageUplaod?: Moment): T {
        site.imageUploadStatus = MonitoringFunctions.calculateImageUploadStatus(dtImageUplaod);
        site.imageUploadStatusName = PeriodicImageUploadStatus[site.imageUploadStatus];

        switch (site.imageUploadStatus) {
            case PeriodicImageUploadStatus.Normal:
                site.imageUploadIcon = "check";
                site.imageUploadIconClass = "normal";
                break;
            case PeriodicImageUploadStatus.Warning:
                site.imageUploadIcon = "image";
                site.imageUploadIconClass = "warning";
                break;
            case PeriodicImageUploadStatus.Error:
                site.imageUploadIcon = "image";
                site.imageUploadIconClass = "error";
                break;
            default:
                site.imageUploadIcon = "question-circle";
                site.imageUploadIconClass = "unknown";
                break;
        }
        return site;
    }

    private static calculateHeartbeatStatus(dtHeartbeat: Moment): HeartbeatStatus {
        if (dtHeartbeat == null) {
            return HeartbeatStatus.Unknown;
        }

        const utcNow: Moment = utc();
        const diff = duration(utcNow.diff(dtHeartbeat, "milliseconds"));
        if (diff < this.heartBeatWarningTimeoutMinutes) {
            return HeartbeatStatus.Normal;
        }
        if (diff < this.heartBeatErrorTimeoutMinutes) {
            return HeartbeatStatus.Warning;
        }
        return HeartbeatStatus.Error;
    }

    private static calculateImageUploadStatus(dtImageUpload: Moment): PeriodicImageUploadStatus {
        if (dtImageUpload == null) {
            return PeriodicImageUploadStatus.Unknown;
        }

        const utcNow: Moment = utc();
        const diff = duration(utcNow.diff(dtImageUpload, "milliseconds"));
        if (diff < this.imageWarningTimeoutMinutes) {
            return PeriodicImageUploadStatus.Normal;
        }
        if (diff < this.imageErrorTimeoutMinutes) {
            return PeriodicImageUploadStatus.Warning;
        }
        return PeriodicImageUploadStatus.Error;
    }
}
